/**
 * Root sub-tabs in Weld Details: IWM (Internal Welding Machine) stores under
 * `b{n}::Root`, the Semi-Automatic Welding Machine (RC) under `b{n}::Root RC`.
 *
 * An empty RC tab seeds itself from IWM once. Equipment-specific fields are
 * never copied — those must be entered by hand for the semi-automatic torch.
 */

export const ROOT_RC_PASS_LABEL = 'Root RC'
export const ROOT_IWM_PASS_LABEL = 'Root'

/** Fields left blank on the RC tab — never copied from IWM. */
export const ROOT_RC_EXCLUDED_FIELDS = [
  'weldingSystem',
  'torchConfiguration',
  'gasFlowRateMin',
  'gasFlowRateMax',
  'gasNozzleOrifice',
  'ctwd',
  'stringOrWeave',
  'heatAngle'
] as const

const EXCLUDED = new Set<string>(ROOT_RC_EXCLUDED_FIELDS)

export type WeldDetailFieldState = {
  fields: Record<string, string>
  units: Record<string, string>
}

export function isRootRcExcludedField(key: string): boolean {
  return EXCLUDED.has(key)
}

export function isRootMisfirePassLabel(label: string): boolean {
  return label === 'Root Misfire' || /^Root Misfire \d+$/.test(label)
}

/** Display name for top-level Weld Details pass tabs. */
export function weldDetailsPassTabLabel(passLabel: string): string {
  if (passLabel === ROOT_IWM_PASS_LABEL || passLabel === ROOT_RC_PASS_LABEL) return 'Root'
  return passLabel
}

/** Display labels for the Root sub-tabs. */
export function weldDetailsRootSubTabLabel(passLabel: string): string {
  if (passLabel === ROOT_IWM_PASS_LABEL) return 'IWM / Automatic'
  if (passLabel === ROOT_RC_PASS_LABEL) return 'Semi-Automatic Root Misfire'
  return passLabel
}

export function isRootPassFamily(passLabel: string): boolean {
  return passLabel === ROOT_IWM_PASS_LABEL || passLabel === ROOT_RC_PASS_LABEL
}

/**
 * Top-level Weld Details tabs from expanded pass sequence:
 * drop Root Misfire*; keep a single Root (IWM/RC are sub-tabs under Root).
 */
export function weldDetailsPassGroupsFromExpanded(expanded: string[]): string[] {
  const out: string[] = []
  for (const label of expanded) {
    if (isRootMisfirePassLabel(label)) continue
    out.push(label)
  }
  return out
}

export function isRcFieldsEffectivelyEmpty(fields: Record<string, string> | null | undefined): boolean {
  return Object.values(fields ?? {}).every((v) => !String(v ?? '').trim())
}

/**
 * Copy IWM field values into RC. Skips excluded keys. By default only fills
 * blank RC fields so saved RC values are never overwritten.
 */
export function copyIwmRootToRcFields(
  iwmFields: Record<string, string>,
  rcFields: Record<string, string>,
  options?: { overwriteEmptyOnly?: boolean }
): Record<string, string> {
  const overwriteEmptyOnly = options?.overwriteEmptyOnly !== false
  const next = { ...rcFields }
  for (const [key, value] of Object.entries(iwmFields)) {
    if (EXCLUDED.has(key)) continue
    const existing = String(next[key] ?? '').trim()
    if (overwriteEmptyOnly && existing) continue
    next[key] = value
  }
  for (const key of ROOT_RC_EXCLUDED_FIELDS) {
    if (!(key in next)) next[key] = ''
  }
  return next
}

/**
 * Seed a full RC state from IWM only when RC fields are empty.
 * Returns null when seeding should not run.
 */
export function seedRootRcFromIwm(
  iwm: WeldDetailFieldState | null | undefined,
  rc: WeldDetailFieldState | null | undefined
): WeldDetailFieldState | null {
  if (!isRcFieldsEffectivelyEmpty(rc?.fields)) return null
  if (isRcFieldsEffectivelyEmpty(iwm?.fields)) return null
  return {
    fields: copyIwmRootToRcFields(iwm!.fields, rc?.fields ?? {}),
    units: { ...(iwm!.units ?? {}), ...(rc?.units ?? {}) }
  }
}

/** Legacy `weldDetails.groups` keys that held the semi-automatic root pass. */
export function legacyRootRcGroupKeys(bevelIndex: number, groupKeys: string[]): string[] {
  const prefix = `b${bevelIndex}::`
  const exact = [`${prefix}Root Misfire`, `${prefix}Root Misfire 1`]
  const numbered = groupKeys
    .filter((key) => new RegExp(`^b${bevelIndex}::Root Misfire \\d+$`).test(key))
    .sort()
  return Array.from(new Set([...exact, ...numbered]))
}
